import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import { Input } from "antd5";

import { SaveListButton } from "components/entities/save_components/lists/SaveListButton";
import PaywallPopover from "components/paywall/PaywallPopover";
import { useDebouncedValue } from "lib/debounce";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { OrgSortBy, OrgSortOrder, SearchOrgRole } from "lib/types/graphQLEnums";
import { ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN } from "lib/types/models";
import { deduplicateByKey, isDefined, simpleArrayDedupe } from "lib/utils";
import SelectedSupplierTags from "./dropdown_list_selects/new_supplier_selects/SelectedSupplierTags";
import SuppliersDropdown from "./dropdown_list_selects/new_supplier_selects/SuppliersDropdown";
import { SupplierOption } from "./dropdown_list_selects/new_supplier_selects/types";
import { SelectProps } from "./Inputs";

import css from "./SupplierSelect.module.scss";

type SimpleOrg = {
  id: string;
  name: string;
};

export function convertOrgToSupplierOption(orgs?: SimpleOrg[] | null): SupplierOption[] {
  if (!orgs) {
    return [];
  }

  return orgs.map((o) => ({
    label: o.name,
    value: o.id,
  }));
}

/**
 * If all competitor guids or all partner guids are selected - add the
 * ALL_PARTNERS and/or ALL_COMPETITORS tokens. Reason being; if a user saves a
 * supplier as a competitor or a partner after creating a saved view - we want
 * to be able to have a token which would include this new guid in any
 * pre-existing views with ALL competitors or ALL partners
 */
export function checkIfAllSignalsSelected(
  selectedSupplierIds: string[],
  competitors: SupplierOption[],
  partners: SupplierOption[],
): string[] {
  if (selectedSupplierIds.length === 0) {
    return [];
  }

  const allCompetitorsGuids = competitors.map((s) => s.value);
  const allPartnersGuids = partners.map((s) => s.value);

  const containsAllCompetitors =
    allCompetitorsGuids.length > 0 &&
    allCompetitorsGuids.every((s) => selectedSupplierIds.includes(s));

  const containsAllPartners =
    allPartnersGuids.length > 0 && allPartnersGuids.every((s) => selectedSupplierIds.includes(s));

  if (containsAllCompetitors) {
    selectedSupplierIds.push(ALL_COMPETITORS_TOKEN);
  }

  if (containsAllPartners) {
    selectedSupplierIds.push(ALL_PARTNERS_TOKEN);
  }

  return selectedSupplierIds;
}

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options">;

export function SupplierSelect<T extends FieldValues>({
  placeholder = "Search competitors, partners and other suppliers",
  hideSaveAsSignals = false,
  ...props
}: FieldProps<T> & {
  allowAccessForAllUsers?: boolean;
  placeholder?: string;
  /** When true, hides the "Save as signals" action. Use on NoticesV2 where signals are not in scope. */
  hideSaveAsSignals?: boolean;
}) {
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText] = useDebouncedValue(searchText, 300);

  const [isOpen, setIsOpen] = useState(false);

  const { field } = useController(props);

  const { authorised: hasSuppliers } = useCheckSubscription("SUPPLIERS", {
    "Context source": "Supplier filter",
  });

  const [selectedSuppliers, setSelectedSuppliers] = useState<SupplierOption[]>([]);

  const { data: signals, isLoading: isLoadingSignals } = useSignalSettings({
    includeAliases: true,
    includeNotices: true,
  });

  const competitors = useMemo(
    (): SupplierOption[] => convertOrgToSupplierOption(signals?.competitors),
    [signals?.competitors],
  );

  const partners = useMemo(
    (): SupplierOption[] => convertOrgToSupplierOption(signals?.partners),
    [signals?.partners],
  );

  /**
   * UseMemo of selectedSupplierIds is primarily used to handle the case where
   * the user selects the ALL_COMPETITORS or ALL_PARTNERS token
   * or it is already present in the filters (from a saved view for example)
   */
  const selectedSupplierIds = useMemo((): string[] => {
    if (!field.value) {
      return [];
    }

    let filterValue: string[] = field.value.filter(isDefined);

    // If ALL_COMPETITORS token exists in filters, then replace it with all competitor IDs
    if (filterValue.includes(ALL_COMPETITORS_TOKEN)) {
      filterValue = filterValue.concat(competitors.map((s) => s.value));
    }

    // If ALL_PARTNERS token exists in filters, then replace it with all partner IDs
    if (filterValue.includes(ALL_PARTNERS_TOKEN)) {
      filterValue = filterValue.concat(partners.map((s) => s.value));
    }

    // Ensure that all partners and all competitors tokens are removed from the filter value
    return simpleArrayDedupe(filterValue).filter(
      (val) => ![ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN].includes(val),
    );
  }, [competitors, field.value, partners]);

  const { data: initialOrganisations } = useOrganisations(
    {
      limit: Math.max(selectedSupplierIds.length, 1),
      page: 1,
      role: SearchOrgRole.Supplier,
      ids: selectedSupplierIds,
      sortOrder: OrgSortOrder.Desc,
      sortBy: OrgSortBy.AwardCount,
      include: { awardCount: true, noticeCount: false, aliases: true },
    },
    {
      enabled: selectedSupplierIds.length > 0 && selectedSuppliers.length === 0,
    },
  );

  // Loads initially selected suppliers
  useEffect(() => {
    if (initialOrganisations) {
      const initialOrgs = initialOrganisations.searchOrganisations.orgs;
      setSelectedSuppliers(initialOrgs.map((o) => ({ label: o.name, value: o.id })));
    }
  }, [initialOrganisations]);

  // The field value is the source of truth. When it changes from outside the component
  // (clearing the filters, loading a saved view) drop any local selections it no longer
  // holds, so a stale selection can't be re-submitted by the next pick in the dropdown.
  useEffect(() => {
    setSelectedSuppliers((current) => {
      const stillSelected = current.filter((s) => selectedSupplierIds.includes(s.value));
      return stillSelected.length === current.length ? current : stillSelected;
    });
  }, [selectedSupplierIds]);

  const handleSuppliersSelection = useCallback(
    (newSuppliers: SupplierOption[]) => {
      const newSuppliersDeuped = Array.from(deduplicateByKey(newSuppliers, (s) => s.value));

      setSelectedSuppliers(newSuppliersDeuped);

      const newSupplierIds = newSuppliersDeuped.map((s) => s.value);

      // Puts in all_competitors and all_partners tokens if all competitors or
      // all partners are selected
      const newSelectionValues = checkIfAllSignalsSelected(newSupplierIds, competitors, partners);
      field.onChange(newSelectionValues);
    },
    [competitors, field, partners],
  );

  const showTags = selectedSupplierIds.length > 0 && !isOpen;

  return (
    <PaywallPopover featureType="SUPPLIERS" isDisabled={props.allowAccessForAllUsers}>
      <div
        aria-label="supplierSelectContainer"
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) {
            return;
          }
          setIsOpen(false);
        }}
      >
        <div tabIndex={0} className={css.container}>
          <Input
            aria-label="supplierSearchInput"
            className={getSupplierInputClass(hasSuppliers, props.allowAccessForAllUsers)}
            onClick={() => {
              if (hasSuppliers) {
                setIsOpen(true);
              }
            }}
            onChange={(e) => {
              const newValue = e.currentTarget.value.trim();

              // if clearing the search text, close the dropdown
              if (newValue.length === 0 && searchText.length > 0) {
                setIsOpen(false);
              }

              // if we're typing, open the dropdown
              if (newValue.length > 0 && !isOpen) {
                setIsOpen(true);
              }

              setSearchText(e.currentTarget.value.trim());
            }}
            placeholder={isOpen ? undefined : placeholder}
          />
          <SuppliersDropdown
            handleSuppliersSelection={handleSuppliersSelection}
            textSearch={debouncedText}
            isOpen={isOpen}
            selectedSuppliers={selectedSuppliers}
            competitors={signals?.competitors ?? []}
            partners={signals?.partners ?? []}
            isLoading={isLoadingSignals}
          />
          {showTags && (
            <>
              <SelectedSupplierTags
                selectedSuppliers={selectedSuppliers}
                handleSuppliersSelection={handleSuppliersSelection}
              />
              {!hideSaveAsSignals && !isRelevancySearchEnabled && (
                <SaveSupplierSignals supplierGuids={selectedSupplierIds} />
              )}
            </>
          )}
        </div>
      </div>
    </PaywallPopover>
  );
}

function getSupplierInputClass(hasSuppliers: boolean, allowAccessForAllUsers?: boolean) {
  if (allowAccessForAllUsers) return css.inputField;
  return hasSuppliers ? css.inputField : css.disabledInputField;
}

type Props = {
  supplierGuids: string[];
};

/**
 * Saves the suppliers currently selected in the filter as competitor or partner signals,
 * via the shared save-to-list drawer.
 */
function SaveSupplierSignals({ supplierGuids }: Props) {
  return (
    <div className={css.saveSignalBtn}>
      <SaveListButton
        entityIds={supplierGuids}
        entityType="supplier"
        trackingData={{ "Context source": "Supplier filter" }}
        buttonProps={{
          buttonType: "secondary",
          buttonText: "Save as signals",
          icon: { icon: "signal01" },
        }}
      />
    </div>
  );
}
